import { useState, useCallback } from 'react';

import {
  TenantScreeningFormState,
  UserRole,
  Address,
  OwnerInfo,
  AgreementAcceptance,
  ScreeningPackage,
  PayeeConfiguration,
  shouldShowOwnerInfoStep,
  calculateCurrentProgress,
} from '../types/tenantScreening';

export type TenantScreeningStep = 1 | 2 | 3 | 4 | 5 | 6 | 7;

interface UseTenantScreeningFlowReturn {
  // Current state
  currentStep: TenantScreeningStep;
  formData: TenantScreeningFormState;
  isLoading: boolean;

  // Navigation
  goToStep: (step: TenantScreeningStep) => void;
  goToNextStep: () => void;
  goToPrevStep: () => void;
  canGoToStep: (step: TenantScreeningStep) => boolean;

  // Form data updates
  updateAddress: (address: Partial<Address>) => void;
  updateUserRole: (role: UserRole) => void;
  updateOwnerInfo: (info: Partial<OwnerInfo>) => void;
  updateAgreement: (agreement: Partial<AgreementAcceptance>) => void;
  updateSelectedPackage: (packageType: ScreeningPackage) => void;
  updatePayeeConfig: (config: Partial<PayeeConfiguration>) => void;

  // Progress tracking
  getCurrentProgress: () => number;
  getStepTitle: (step: TenantScreeningStep) => string;
  getStepDescription: (step: TenantScreeningStep) => string;

  // Form validation
  isStepValid: (step: TenantScreeningStep) => boolean;
  isFormComplete: () => boolean;

  // Actions
  submitForm: () => Promise<void>;
  resetForm: () => void;
}

const STEP_TITLES: Record<TenantScreeningStep, string> = {
  1: 'Where is your property located?',
  2: 'What is your role?',
  3: 'Property Owner Information',
  4: 'TransUnion Service Agreement',
  5: 'Choose your screening package',
  6: 'Payment & Application Setup',
  7: 'Success!',
};

const STEP_DESCRIPTIONS: Record<TenantScreeningStep, string> = {
  1: 'Enter the complete address of the property you want to screen tenants for.',
  2: 'Select your relationship to the property to customize the screening process.',
  3: "Please provide the property owner's contact information for the screening process.",
  4: 'Please review and accept the terms of service to continue with the tenant screening process.',
  5: 'Select the tenant screening package that best fits your needs. All packages include secure online applications and instant notifications.',
  6: 'Configure who will pay for the screening and choose your application type.',
  7: 'Your screening application is ready. Share the link below with prospective tenants to get started.',
};

export const useTenantScreeningFlow = (): UseTenantScreeningFlowReturn => {
  const [currentStep, setCurrentStep] = useState<TenantScreeningStep>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<TenantScreeningFormState>({});

  // Navigation functions
  const canGoToStep = useCallback(
    (step: TenantScreeningStep): boolean => {
      // Can always go to step 1
      if (step === 1) return true;

      // For other steps, check if previous steps are valid
      for (let i = 1; i < step; i++) {
        // Inline validation logic to avoid circular dependency
        switch (i) {
          case 1:
            if (!formData.address?.fullAddress?.trim()) return false;
            break;
          case 2:
            if (!formData.userRole) return false;
            break;
          case 3:
            // Skip validation if step shouldn't be shown
            if (
              formData.userRole &&
              !shouldShowOwnerInfoStep(formData.userRole)
            ) {
              continue;
            }
            if (
              !(
                formData.ownerInfo?.firstName?.trim() &&
                formData.ownerInfo?.lastName?.trim() &&
                formData.ownerInfo?.email?.trim() &&
                formData.ownerInfo?.phone?.trim()
              )
            )
              return false;
            break;
          case 4:
            if (!formData.agreement?.accepted) return false;
            break;
          case 5:
            if (!formData.selectedPackage) return false;
            break;
          case 6:
            if (
              !(
                formData.payeeConfig?.paymentResponsibility &&
                formData.payeeConfig?.applicationType
              )
            )
              return false;
            break;
          default:
            break;
        }
      }

      return true;
    },
    [formData]
  );

  const goToStep = useCallback(
    (step: TenantScreeningStep) => {
      if (canGoToStep(step)) {
        setCurrentStep(step);
      }
    },
    [canGoToStep]
  );

  const goToNextStep = useCallback(() => {
    if (currentStep < 7) {
      const nextStep = (currentStep + 1) as TenantScreeningStep;

      // Skip owner info step if user is landlord
      if (
        nextStep === 3 &&
        formData.userRole &&
        !shouldShowOwnerInfoStep(formData.userRole)
      ) {
        setCurrentStep(4);
      } else {
        setCurrentStep(nextStep);
      }
    }
  }, [currentStep, formData.userRole]);

  const goToPrevStep = useCallback(() => {
    if (currentStep > 1) {
      const prevStep = (currentStep - 1) as TenantScreeningStep;

      // Skip owner info step if user is landlord (going backwards)
      if (
        prevStep === 3 &&
        formData.userRole &&
        !shouldShowOwnerInfoStep(formData.userRole)
      ) {
        setCurrentStep(2);
      } else {
        setCurrentStep(prevStep);
      }
    }
  }, [currentStep, formData.userRole]);

  // Form data update functions
  const updateAddress = useCallback((address: Partial<Address>) => {
    setFormData(prev => ({
      ...prev,
      address: { ...prev.address, ...address },
    }));
  }, []);

  const updateUserRole = useCallback((role: UserRole) => {
    setFormData(prev => ({ ...prev, userRole: role }));
  }, []);

  const updateOwnerInfo = useCallback((info: Partial<OwnerInfo>) => {
    setFormData(prev => ({
      ...prev,
      ownerInfo: { ...prev.ownerInfo, ...info },
    }));
  }, []);

  const updateAgreement = useCallback(
    (agreement: Partial<AgreementAcceptance>) => {
      setFormData(prev => ({
        ...prev,
        agreement: { ...prev.agreement, ...agreement },
      }));
    },
    []
  );

  const updateSelectedPackage = useCallback((packageType: ScreeningPackage) => {
    setFormData(prev => ({ ...prev, selectedPackage: packageType }));
  }, []);

  const updatePayeeConfig = useCallback(
    (config: Partial<PayeeConfiguration>) => {
      setFormData(prev => ({
        ...prev,
        payeeConfig: { ...prev.payeeConfig, ...config },
      }));
    },
    []
  );

  // Progress and validation functions
  const getCurrentProgress = useCallback((): number => {
    if (!formData.userRole) return 0;
    return calculateCurrentProgress(currentStep, formData.userRole);
  }, [currentStep, formData.userRole]);

  const getStepTitle = useCallback((step: TenantScreeningStep): string => {
    return STEP_TITLES[step];
  }, []);

  const getStepDescription = useCallback(
    (step: TenantScreeningStep): string => {
      return STEP_DESCRIPTIONS[step];
    },
    []
  );

  const isStepValid = useCallback(
    (step: TenantScreeningStep): boolean => {
      switch (step) {
        case 1:
          return !!formData.address?.fullAddress?.trim();
        case 2:
          return !!formData.userRole;
        case 3:
          // Skip validation if step shouldn't be shown
          if (
            formData.userRole &&
            !shouldShowOwnerInfoStep(formData.userRole)
          ) {
            return true;
          }
          return !!(
            formData.ownerInfo?.firstName?.trim() &&
            formData.ownerInfo?.lastName?.trim() &&
            formData.ownerInfo?.email?.trim() &&
            formData.ownerInfo?.phone?.trim()
          );
        case 4:
          return !!formData.agreement?.accepted;
        case 5:
          return !!formData.selectedPackage;
        case 6:
          return !!(
            formData.payeeConfig?.paymentResponsibility &&
            formData.payeeConfig?.applicationType
          );
        case 7:
          return true; // Success step is always valid
        default:
          return false;
      }
    },
    [formData]
  );

  const isFormComplete = useCallback((): boolean => {
    return [1, 2, 3, 4, 5, 6].every(step =>
      isStepValid(step as TenantScreeningStep)
    );
  }, [isStepValid]);

  // Actions
  const submitForm = useCallback(async (): Promise<void> => {
    if (!isFormComplete()) {
      throw new Error('Form is not complete');
    }

    setIsLoading(true);
    try {
      // In real implementation, this would submit to API
      await new Promise(resolve => setTimeout(resolve, 1000));
      // console.log('Form submitted:', formData);
    } catch (error) {
      console.error('Form submission error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [isFormComplete]);

  const resetForm = useCallback(() => {
    setFormData({});
    setCurrentStep(1);
    setIsLoading(false);
  }, []);

  return {
    // Current state
    currentStep,
    formData,
    isLoading,

    // Navigation
    goToStep,
    goToNextStep,
    goToPrevStep,
    canGoToStep,

    // Form data updates
    updateAddress,
    updateUserRole,
    updateOwnerInfo,
    updateAgreement,
    updateSelectedPackage,
    updatePayeeConfig,

    // Progress tracking
    getCurrentProgress,
    getStepTitle,
    getStepDescription,

    // Form validation
    isStepValid,
    isFormComplete,

    // Actions
    submitForm,
    resetForm,
  };
};
